import React, { useEffect, useState } from 'react';
import { Box, TextField, Checkbox, Typography, Card, FormHelperText } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ClientPointsRequest, ClientPoints, IPaymentRequest, IPayment } from "@ontime/types";
import CustomDialogTitle from '../../customComponents/CustomDialogTitle';
import CustomDialogContent from '../../customComponents/CustomDialogContent';
import CustomFormAction from '../../customComponents/CustomFormAction';
import CustomButton from '../../customComponents/CustomButton';
import InfoComponent from '../../customComponents/InfoComponent';
import PaymentService from '../../services/PaymentService';
import { useSelector } from 'react-redux';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectLoyaltyPoints } from '../../stateManagement/loyaltyPointSlice';
import { PaymentMethod, PaymentStatus } from "@ontime/types";

const paymentSchema = z.object({
    paymentId: z.string(),
    organizationId: z.string(),
    appointmentId: z.string(),
    clientId: z.string(),

    note: z.string().max(200, "Maximum length of Note is 200 characters.").optional(),
    status: z.nativeEnum(PaymentStatus),
    method: z.nativeEnum(PaymentMethod),

    amount: z.number().min(0.1, "Minimum amount payment is 0.1"),

    discountAmount: z.number().optional(),
    surchargeAmount: z.number().optional(),
    tip: z.number().optional(),
    taxPercent: z.number().max(100, "Maximum is 100.").optional(),
    usedPointsCount: z.number().optional(),
    reductionAmountByUsedPoints: z.number().optional(),
});

interface AppointmentPaymentFormFormProps {
    organizationId?: string;
    clientId?: string;
    appointmentId?: string;
    appointmentAmount?: number;
    paymentInput?: IPaymentRequest;
    closeForm: (input?: IPayment) => void;
}

const AppointmentPaymentForm: React.FC<AppointmentPaymentFormFormProps> = ({ organizationId, appointmentId, paymentInput, closeForm, appointmentAmount, clientId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);

    const organization = useSelector(selectSelectedOrganization);
    const LoyaltyPoints = useSelector(selectLoyaltyPoints);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<IPaymentRequest>({
        resolver: zodResolver(paymentSchema),
        defaultValues: {
            paymentId: '',
            organizationId: organizationId,
            appointmentId: appointmentId,
            clientId: clientId,

            note: '',
            status: PaymentStatus.Pending,
            method: PaymentMethod.CreditCard,

            amount: appointmentAmount ?? 0,

            discountAmount: 0,
            surchargeAmount: 0,
            tip: 0,
            taxPercent: organization.defaultTaxPercent ?? 0,
            usedPointsCount: 0,
            reductionAmountByUsedPoints: 0,
        },
    });

    useEffect(() => {
        console.log('useEffect', paymentInput)
        if (paymentInput?.paymentId) {
            reset({
                paymentId: paymentInput.paymentId,
                organizationId: organizationId,
                appointmentId: paymentInput.appointmentId,
                clientId: clientId,

                note: paymentInput.note,
                status: paymentInput.status ?? PaymentStatus.Pending,
                method: paymentInput.method ?? PaymentMethod.CreditCard,

                amount: paymentInput.amount ?? 0,

                discountAmount: paymentInput.discountAmount,
                surchargeAmount: paymentInput.surchargeAmount,
                tip: paymentInput.tip,
                taxPercent: paymentInput.taxPercent,
                usedPointsCount: 0,
                reductionAmountByUsedPoints: 0,
            });

            setUsePercentDiscount(false);
            setUsePercentTip(false);
        }
        setLoading(false);

        if (clientId && LoyaltyPoints.length) {
            GetClientPoints({
                clientId: clientId ?? '',
                organizationId: organizationId ?? '',
                paymentId: paymentInput?.paymentId ?? '0',
                appointmentId: appointmentId
            })
        }
        else {
            setLoadingGetClientPoints(false);
        }

    }, [paymentInput, reset]);


    const [loadingGetClientPoints, setLoadingGetClientPoints] = useState(true);
    const [clientPoints, setClientPoints] = useState<ClientPoints>({});
    const [usePoints, setUsePoints] = useState(false);

    const GetClientPoints = async (input: ClientPointsRequest) => {
        console.log('GetClientPoints', input)
        setLoadingGetClientPoints(true);
        try {
            const response = await PaymentService.GetClientPoints(input);
            setClientPoints(response);
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoadingGetClientPoints(false);
        }
    };

    const handleSave = async (data: IPaymentRequest) => {
        setLoading(true);

        const formData = {
            ...data,
            organizationId: organizationId ?? '',
            appointmentId: appointmentId ?? '',
            paymentId: paymentInput?.paymentId ?? ''
        };

        try {
            if (paymentInput?.paymentId) {
                const response = await PaymentService.Update(organization.organizationId, appointmentId ?? '', formData);

                closeForm(response);
            } else {
                const response = await PaymentService.Create(organization.organizationId, appointmentId ?? '', formData);

                closeForm(response);
            }
        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };


    const [discountAmountPercent, setDiscountAmountPercent] = useState(0);
    const [usePercentDiscount, setUsePercentDiscount] = useState(true);
    const [tipPercent, setTipPercent] = useState(0);
    const [usePercentTip, setUsePercentTip] = useState(true);

    const handleSetDiscountAmount = (input: number) => {
        setValue('discountAmount', input);
        setDiscountAmountPercent(0);
    }

    const handleSetTip = (input: number) => {
        setValue('tip', input);
        setTipPercent(0);
    }

    useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (usePercentDiscount && (name === 'amount' || name === 'surchargeAmount')) {
                const baseAmount = (value.amount ?? 0) + (value.surchargeAmount ?? 0);
                setValue('discountAmount', Number(((baseAmount * discountAmountPercent) / 100).toFixed(2)));
            }
            if (usePercentTip && (name === 'amount' || name === 'surchargeAmount')) {
                const baseAmount = (value.amount ?? 0) + (value.surchargeAmount ?? 0);
                setValue('tip', Number(((baseAmount * tipPercent) / 100).toFixed(2)));
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, usePercentDiscount, discountAmountPercent, usePercentTip, tipPercent, setValue]);

    return (
        <Box>
            <CustomDialogTitle handleClose={closeForm} title={paymentInput?.paymentId ? 'Edit Payment' : 'New Payment'} loading={loading || loadingGetClientPoints} dontShowInfoButton={true} />
            <CustomDialogContent>
                <form onSubmit={handleSubmit(handleSave)} noValidate>
                    <Card style={{ padding: '10px 0' }}>
                        <Box display='flex' flexDirection='row' justifyContent='space-around'>
                            <Box width='47%' >
                                <Box mt={2}>
                                    <InfoComponent infoContent='Base payment amount.(Required)' >
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Amount"
                                            InputLabelProps={{ shrink: true }}
                                            {...register('amount', { valueAsNumber: true })}
                                            error={!!errors.amount}
                                            helperText={errors.amount?.message}
                                            inputProps={{ step: "0.01", min: 0 }}
                                        />
                                    </InfoComponent>
                                </Box>
                                <Box mt={2}>
                                    <InfoComponent infoContent='Optional additional fees.' >
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Surcharge Amount"
                                            InputLabelProps={{ shrink: true }}
                                            {...register('surchargeAmount', { valueAsNumber: true })}
                                            error={!!errors.surchargeAmount}
                                            helperText={errors.surchargeAmount?.message}
                                            inputProps={{ step: "0.01", min: 0 }}
                                        />
                                    </InfoComponent>
                                </Box>
                                <InfoComponent infoContent='Checkbox + select to use a % discount (e.g., 20%).' >
                                    <Box mt={2} display='flex'>
                                        <Checkbox
                                            checked={usePercentDiscount}
                                            onChange={(e) => {
                                                setUsePercentDiscount(e.target.checked);
                                                if (e.target.checked) {
                                                    const percent = discountAmountPercent;
                                                    const baseAmount = watch('amount') + (watch('surchargeAmount') ?? 0);
                                                    setValue('discountAmount', Number(((baseAmount * percent) / 100).toFixed(2)));
                                                }
                                            }}
                                        />
                                        <TextField
                                            select
                                            fullWidth
                                            label="Discount Percent"
                                            value={discountAmountPercent}
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                setDiscountAmountPercent(val);
                                                const baseAmount = watch('amount') + (watch('surchargeAmount') ?? 0);
                                                setValue('discountAmount', Number(((baseAmount * val) / 100).toFixed(2)));
                                            }}
                                            SelectProps={{ native: true }}
                                            disabled={!usePercentDiscount}
                                        >
                                            {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => (
                                                <option key={value} value={value}>{value}%</option>
                                            ))}
                                        </TextField>

                                    </Box>
                                </InfoComponent>
                                <Box mt={2}>
                                    <InfoComponent infoContent='Computed (if % is checked) or editable.' >
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Discount Amount"
                                            InputLabelProps={{ shrink: true }}
                                            value={watch('discountAmount')}
                                            onChange={(e) => handleSetDiscountAmount(Number(e.target.value))}
                                            error={!!errors.discountAmount}
                                            helperText={errors.discountAmount?.message}
                                            inputProps={{ step: "0.01", min: 0 }}
                                            disabled={usePercentDiscount}
                                        />
                                    </InfoComponent>
                                </Box>
                                {(clientPoints.pointAmount ?? 0) > 0 &&
                                    <>
                                        <InfoComponent infoContent='Checkbox to apply client’s loyalty points as a discount.' >
                                            <Box mt={2} display='flex'>
                                                <Checkbox
                                                    checked={usePoints}
                                                    onChange={(e) => {
                                                        setUsePoints(e.target.checked);
                                                        if (e.target.checked && clientPoints?.pointAmount) {
                                                            setValue('reductionAmountByUsedPoints', clientPoints.pointAmount);
                                                            setValue('usedPointsCount', clientPoints.points ?? 0);
                                                        } else {
                                                            setValue('reductionAmountByUsedPoints', 0);
                                                            setValue('usedPointsCount', 0);
                                                        }
                                                    }}
                                                />
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    label="Discount Points Amount"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={clientPoints.pointAmount}
                                                    error={!!errors.surchargeAmount}
                                                    helperText={errors.surchargeAmount?.message}
                                                    inputProps={{ step: "0.01", min: 0 }}
                                                    disabled
                                                />
                                            </Box>
                                        </InfoComponent>
                                        <FormHelperText sx={{ ml: 0.5, color: 'green' }}>
                                            {`(${clientPoints.points ?? 0} worth of points)`}
                                        </FormHelperText>
                                    </>}
                            </Box>
                            <Box width='47%' display='flex' justifyContent='flex-start' alignItems='flex-end'>
                                <Box mt={2}>
                                    <InfoComponent infoContent='Subtotal: Amount + Surcharge - Discount - Points' >
                                        <Typography>
                                            Total = {Number(((watch('amount') ?? 0) + (watch('surchargeAmount') ?? 0) - (watch('discountAmount') ?? 0) - (watch('reductionAmountByUsedPoints') ?? 0)).toFixed(2))}
                                        </Typography>
                                    </InfoComponent>
                                </Box>
                            </Box>
                        </Box>
                    </Card>
                    <Card style={{ padding: '10px 0', marginTop: '10px' }}>
                        <Box display='flex' flexDirection='row' justifyContent='space-around'>
                            <Box width='47%' >
                                <Box mt={2}>
                                    <InfoComponent infoContent='User-defined percentage in places or editable. (e.g., 5%)' >
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="taxPercent"
                                            InputLabelProps={{ shrink: true }}
                                            {...register('taxPercent', { valueAsNumber: true })}
                                            error={!!errors.taxPercent}
                                            helperText={errors.taxPercent?.message}
                                            inputProps={{ step: "0.01", min: 0 }}
                                        />
                                    </InfoComponent>
                                </Box>
                                <Box mt={2}>
                                    <InfoComponent infoContent='Auto-calculated: subtotal * taxPercent / 100' >
                                        <TextField
                                            type="number"
                                            disabled
                                            label="Tax Amount"
                                            variant="outlined"
                                            value={Number(
                                                (
                                                    ((watch('amount') ?? 0) +
                                                        (watch('surchargeAmount') ?? 0) -
                                                        (watch('discountAmount') ?? 0) -
                                                        (watch('reductionAmountByUsedPoints') ?? 0)) *
                                                    ((watch('taxPercent') ?? 0) / 100)
                                                ).toFixed(2)
                                            )} fullWidth
                                        />
                                    </InfoComponent>
                                </Box>
                            </Box>
                            <Box width='47%' display='flex' justifyContent='flex-start' alignItems='flex-end'>
                                <Box mt={2}>
                                    <InfoComponent infoContent='subtotal + tax' >
                                        <Typography>
                                            Total = {Number(
                                                (
                                                    ((watch('amount') ?? 0) +
                                                        (watch('surchargeAmount') ?? 0) -
                                                        (watch('discountAmount') ?? 0) -
                                                        (watch('reductionAmountByUsedPoints') ?? 0)) *
                                                    (1 + (watch('taxPercent') ?? 0) / 100)
                                                ).toFixed(2)
                                            )}
                                        </Typography>
                                    </InfoComponent>
                                </Box>
                            </Box>
                        </Box>
                    </Card>
                    <Card style={{ padding: '10px 0', marginTop: '10px' }}>
                        <Box display='flex' flexDirection='row' justifyContent='space-around'>
                            <Box width='47%' >
                                <InfoComponent infoContent='Like discount, allows tip as a % of subtotal' >
                                    <Box mt={2} display='flex'>
                                        <Checkbox
                                            checked={usePercentTip}
                                            onChange={(e) => {
                                                setUsePercentTip(e.target.checked);
                                                if (e.target.checked) {
                                                    const percent = tipPercent;
                                                    const baseAmount = watch('amount') + (watch('surchargeAmount') ?? 0);
                                                    setValue('tip', Number(((baseAmount * percent) / 100).toFixed(2)));
                                                }
                                            }}
                                        />
                                        <TextField
                                            select
                                            fullWidth
                                            label="Tip Percent"
                                            value={tipPercent}
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                setTipPercent(val);
                                                const baseAmount = watch('amount') + (watch('surchargeAmount') ?? 0);
                                                setValue('tip', Number(((baseAmount * val) / 100).toFixed(2)));
                                            }}
                                            SelectProps={{ native: true }}
                                            disabled={!usePercentTip}
                                        >
                                            {[0, 5, 10, 15, 20, 25].map((value) => (
                                                <option key={value} value={value}>{value}%</option>
                                            ))}
                                        </TextField>
                                    </Box>
                                </InfoComponent>
                                <Box mt={2}>
                                    <InfoComponent infoContent='Editable if not using percentage'>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="tip"
                                            InputLabelProps={{ shrink: true }}
                                            value={watch('tip')}
                                            onChange={(e) => handleSetTip(Number(e.target.value))}
                                            error={!!errors.tip}
                                            helperText={errors.tip?.message}
                                            inputProps={{ step: "0.01", min: 0 }}
                                            disabled={usePercentTip}
                                        />
                                    </InfoComponent>
                                </Box>
                            </Box>
                            <Box width='47%' display='flex' justifyContent='flex-start' alignItems='flex-end'>
                                <Box mt={2}>
                                    <InfoComponent infoContent='Total With Tax + Tip' >
                                        <Typography>
                                            Total = {Number((((((watch('amount') ?? 0) + (watch('surchargeAmount') ?? 0) - (watch('discountAmount') ?? 0) - (watch('reductionAmountByUsedPoints') ?? 0)) * (1 + (watch('taxPercent') ?? 0) / 100)) + (watch('tip') ?? 0)).toFixed(2)))}
                                        </Typography>
                                    </InfoComponent>
                                </Box>
                            </Box>
                        </Box>
                    </Card>
                    <Box display="flex" alignItems="center" gap={2} mt={2}>
                        <TextField disabled={loading || loadingGetClientPoints} fullWidth label="Note" InputLabelProps={{ shrink: true }} multiline rows={3} {...register('note')} error={!!errors.note} helperText={errors.note?.message} />
                    </Box>

                    <CustomFormAction>
                        <CustomButton lable='Save payment' type='submit' disabled={loading || loadingGetClientPoints} infoContent={`Save the ${paymentInput?.paymentId ? 'updated' : 'new'} payment record`} />
                    </CustomFormAction>

                </form>
            </CustomDialogContent>
        </Box>
    );
};

export default AppointmentPaymentForm;